import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { AlertTriangle, Lock, PanelLeftClose, PanelLeft } from 'lucide-react';
import { ConversationSidebar } from '../molecules/ConversationSidebar';
import { MessageThread } from '../molecules/MessageThread';
import { ChatInput } from '../atoms/ChatInput';
import {
  useConversationList,
  useConversationMessages,
  useCreateConversation,
  useUpdateConversation,
  useDeleteConversation,
  useAssistantQueryHelpers,
} from '../../../hooks/useConversations';
import { assistantService } from '../../../services/assistantService';
import { mcpService } from '../../../services/mcpService';
import { useLanguage } from '../../../features/language/useLanguage';
import { cn } from '../../../utils/cn';
import type { McpChatMessage, MessageAttachment } from '../../../types/assistant';
import type { DisplayAttachment } from '../molecules/MessageAttachments';

interface DisplayMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  attachments?: DisplayAttachment[];
}

const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar { width: 6px; }
  .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(156,163,175,0.2); border-radius: 20px; }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(156,163,175,0.4); }
`;

export const QoraAssistantShell: React.FC = () => {
  const { t } = useLanguage();
  // PRD 76: Qora assistant has no usage gate; tier-based blocking removed.
  const canWrite = true;
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [optimisticMessages, setOptimisticMessages] = useState<DisplayMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isEditingTopTitle, setIsEditingTopTitle] = useState(false);
  const [topTitleDraft, setTopTitleDraft] = useState('');
  const [imageEnabled, setImageEnabled] = useState(false);

  useEffect(() => {
    let active = true;
    assistantService.getCapabilities().then(caps => {
      if (active) setImageEnabled(!!caps.vision_supported);
    });
    return () => { active = false; };
  }, []);

  const topTitleInputRef = useRef<HTMLInputElement>(null);

  const { data: conversations = [], isLoading: convLoading } = useConversationList();
  const { data: serverMessages = [], isLoading: msgsLoading } = useConversationMessages(activeConversationId);
  const createConv = useCreateConversation();
  const updateConv = useUpdateConversation();
  const deleteConv = useDeleteConversation();
  const { invalidateConversations, invalidateMessages } = useAssistantQueryHelpers();

  // Merge server messages with optimistic local messages for display
  const displayMessages: DisplayMessage[] = React.useMemo(() => {
    const fromServer: DisplayMessage[] = serverMessages
      .filter(m => m.role !== 'system')
      .map(m => ({
        id: m.id,
        role: m.role as 'user' | 'assistant' | 'tool',
        content: m.content,
        attachments: m.attachments?.map(a => ({ path: a.path, mime: a.mime, name: a.name })),
      }));

    // De-duplicate: only show optimistic messages whose ids are not yet in server data
    const serverIds = new Set(fromServer.map(m => m.id));
    const newOptimistic = optimisticMessages.filter(m => !serverIds.has(m.id));

    return [...fromServer, ...newOptimistic];
  }, [serverMessages, optimisticMessages]);

  // Clear optimistic messages when server catches up
  useEffect(() => {
    if (!isSending && optimisticMessages.length > 0) {
      setOptimisticMessages([]);
    }
  }, [serverMessages, isSending, optimisticMessages.length]);

  const handleNewChat = useCallback(() => {
    setActiveConversationId(null);
    setOptimisticMessages([]);
    setIsEditingTopTitle(false);
  }, []);

  const handleSelectConversation = useCallback((id: string) => {
    if (id === activeConversationId) return;
    setActiveConversationId(id);
    setOptimisticMessages([]);
    setIsEditingTopTitle(false);
  }, [activeConversationId]);

  const handlePin = useCallback(
    (id: string, isPinned: boolean) => {
      updateConv.mutate({ id, data: { is_pinned: !isPinned } });
    },
    [updateConv]
  );

  const handleDelete = useCallback(
    (id: string) => {
      deleteConv.mutate(id);
      if (activeConversationId === id) {
        setActiveConversationId(null);
        setOptimisticMessages([]);
      }
    },
    [deleteConv, activeConversationId]
  );

  const handleRename = useCallback(
    (id: string, newTitle: string) => {
      updateConv.mutate({ id, data: { title: newTitle } });
    },
    [updateConv]
  );

  const handleSendMessage = useCallback(
    async (text: string, attachments: MessageAttachment[] = []) => {
      if (isSending) return;

      let convId = activeConversationId;
      let isNewConv = false;

      if (!convId) {
        try {
          const conv = await createConv.mutateAsync(undefined);
          convId = conv.id;
          isNewConv = true;
          setActiveConversationId(convId);
        } catch {
          return;
        }
      }

      // Optimistic user message. The images are already in GCS by send time, so the
      // thumbnail fetches by `path` via useAssistantImage (do NOT pass the auth-gated
      // `a.url` as an <img> src — it would 401). A brief shimmer is acceptable.
      const optimisticAttachments: DisplayAttachment[] = attachments.map(a => ({
        path: a.path,
        mime: a.mime,
        name: a.name,
      }));
      const userMsg: DisplayMessage = {
        id: `opt-user-${Date.now()}`,
        role: 'user',
        content: text,
        attachments: optimisticAttachments.length > 0 ? optimisticAttachments : undefined,
      };
      setOptimisticMessages(prev => [...prev, userMsg]);
      setIsSending(true);

      try {
        const response = await assistantService.sendMessage(convId, text, attachments);

        if (response.conversation_id !== convId) {
          setActiveConversationId(response.conversation_id);
        }

        const generated: DisplayMessage[] = response.messages
          .filter((m: McpChatMessage) => m.role === 'assistant' || m.role === 'tool')
          .map((m: McpChatMessage, i: number) => ({
            id: `opt-gen-${Date.now()}-${i}`,
            role: m.role as 'user' | 'assistant' | 'tool',
            content:
              m.content ||
              (m.tool_calls
                ? `Calling tools: ${m.tool_calls.map(tc => tc.function.name).join(', ')}`
                : ''),
          }));

        setOptimisticMessages(prev => [...prev, ...generated]);

        const existingConv = conversations.find(c => c.id === convId);
        if (isNewConv || (existingConv && !existingConv.title)) {
          const autoTitle = text.length > 60 ? text.substring(0, 57) + '…' : (text || 'Image');
          updateConv.mutate({ id: convId, data: { title: autoTitle } });
        }

        invalidateConversations();
        invalidateMessages(response.conversation_id || convId);
      } catch (err) {
        console.error('Failed to send message:', err);
        const errorMsg: DisplayMessage = {
          id: `opt-err-${Date.now()}`,
          role: 'assistant',
          content: err instanceof Error ? err.message : 'Sorry, something went wrong. Please try again.',
        };
        setOptimisticMessages(prev => [...prev, errorMsg]);
      } finally {
        setIsSending(false);
      }
    },
    [activeConversationId, isSending, createConv, conversations, updateConv, invalidateConversations, invalidateMessages]
  );

  const hasMessages = displayMessages.length > 0;

  const sortedConversations = useMemo(() => {
    return [...conversations].sort((a, b) => {
      if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
      const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return bTime - aTime;
    });
  }, [conversations]);

  const activeConversation = useMemo(
    () => conversations.find(c => c.id === activeConversationId) ?? null,
    [conversations, activeConversationId]
  );

  const canEditTopTitle = !!activeConversation && !createConv.isPending && !isSending;

  useEffect(() => {
    if (isEditingTopTitle && topTitleInputRef.current) {
      topTitleInputRef.current.focus();
      topTitleInputRef.current.select();
    }
  }, [isEditingTopTitle]);

  const startEditTopTitle = () => {
    if (!canEditTopTitle || !activeConversation) return;
    setTopTitleDraft(activeConversation.title || '');
    setIsEditingTopTitle(true);
  };

  const saveTopTitle = () => {
    if (!activeConversation) {
      setIsEditingTopTitle(false);
      return;
    }
    const trimmed = topTitleDraft.trim();
    if (trimmed && trimmed !== activeConversation.title) {
      updateConv.mutate({ id: activeConversation.id, data: { title: trimmed } });
    }
    setIsEditingTopTitle(false);
  };

  const cancelEditTopTitle = () => {
    setIsEditingTopTitle(false);
  };

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div
        className="flex h-full w-full bg-[#0a0a0f] rounded-2xl overflow-hidden border border-white/5 shadow-2xl"
        data-test-id="qora-assistant-shell"
      >
        {/* Sidebar */}
        <div
          className={cn(
            'transition-all duration-300 shrink-0',
            sidebarCollapsed ? 'w-0 overflow-hidden' : 'w-72'
          )}
        >
          <ConversationSidebar
            conversations={sortedConversations}
            activeId={activeConversationId}
            isLoading={convLoading}
            onSelect={handleSelectConversation}
            onNewChat={handleNewChat}
            onPin={handlePin}
            onDelete={handleDelete}
            onRename={handleRename}
          />
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col min-w-0 relative">
          {/* Top bar */}
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 shrink-0">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
              title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
              data-test-id="sidebar-toggle"
            >
              {sidebarCollapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </button>

            <div className="flex-1 min-w-0">
              {isEditingTopTitle && activeConversation ? (
                <input
                  ref={topTitleInputRef}
                  value={topTitleDraft}
                  onChange={e => setTopTitleDraft(e.target.value)}
                  onBlur={saveTopTitle}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveTopTitle();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelEditTopTitle();
                    }
                  }}
                  className="w-full text-sm font-medium bg-white/5 text-white border border-indigo-500/50 rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500"
                  data-test-id="top-bar-title-input"
                />
              ) : (
                <p
                  className={cn(
                    'text-sm font-medium text-white truncate select-none',
                    canEditTopTitle && 'cursor-text'
                  )}
                  onDoubleClick={startEditTopTitle}
                  title={canEditTopTitle ? 'Double-click to rename' : undefined}
                  data-test-id="top-bar-title"
                >
                  {activeConversation?.title || 'Qora Assistant'}
                </p>
              )}
            </div>
          </div>

          {/* Disconnected warning */}
          {!mcpService.hasActiveKey() && (
            <div className="flex items-center gap-2 px-4 py-2 mx-4 mt-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-sm shrink-0">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                Not connected.{' '}
                <a href="/settings" className="underline hover:text-amber-200 font-medium">
                  Connect API Key
                </a>{' '}
                to use the assistant.
              </span>
            </div>
          )}

          {/* Background decorations */}
          <div className="absolute top-0 right-0 w-[30%] h-[30%] bg-indigo-600/5 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[30%] h-[30%] bg-purple-600/5 rounded-full blur-[100px] pointer-events-none" />

          {/* Messages */}
          <div className="flex-1 flex flex-col min-h-0 px-4 py-4 relative z-10">
            <MessageThread
              messages={displayMessages}
              isLoading={isSending}
              isEmpty={!hasMessages && !msgsLoading}
            />
          </div>

          {/* Input */}
          <div className="px-4 pb-4 shrink-0 relative z-10">
            {!canWrite && (
              <div
                className="mb-3 flex items-center gap-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-sm px-3 py-2"
                data-test-id="qora-upgrade-banner"
              >
                <Lock className="h-4 w-4 shrink-0" />
                <span>
                  {t('subscription.upgrade_to_use')}{' '}
                  <a
                    href="/settings/subscription"
                    className="underline hover:text-amber-200 font-medium"
                  >
                    {t('subscription.banner.cta_claim')}
                  </a>
                </span>
              </div>
            )}
            <ChatInput
              onSend={handleSendMessage}
              disabled={isSending || !canWrite}
              showQuickActions={canWrite}
              imageEnabled={imageEnabled && canWrite}
            />
          </div>
        </div>
      </div>
    </>
  );
};
